import { forwardRef, useEffect, useLayoutEffect, useRef } from 'react';
import type { PropsWithoutRef } from 'react';
import styled from 'styled-components';

import {
  useConsolidatedRef,
  useContentTabIndex,
  useEscape,
  useI18n,
  useOuterEvent,
  useUID
} from '../../hooks';
import { getActiveElement, focusNonInteractiveElement } from '../../utils';
import Button from '../Button';
import Text from '../Text';
import Progress from '../Progress';
import AdditionalInfo from '../AdditionalInfo';
import type { RefElement } from '../../types';
import Flex from '../Flex';

import {
  StyledChildrenWrap,
  StyledDialogContent,
  StyledDialogFooter,
  StyledDialogHeader
} from './Dialog.styles';
import type { FormDialogProps } from './Dialog.types';
import Dialog from './Dialog';

export const StyledFormDialog = styled(Dialog)``;

const FormDialog = forwardRef<RefElement<FormDialogProps>, PropsWithoutRef<FormDialogProps>>(
  function FormDialog(
    {
      arrow = true,
      target,
      heading,
      'aria-label': ariaLabel,
      children,
      progress,
      onDismiss,
      onCancel,
      onSubmit,
      additionalInfo,
      ...restProps
    },
    ref
  ) {
    const t = useI18n();
    const headingId = useUID();
    const headingRef = useRef<HTMLHeadingElement>(null);
    const dialogRef = useConsolidatedRef(ref);
    const dialogContentRef = useRef<HTMLDivElement>(null);
    const contentTabIndex = useContentTabIndex(dialogRef, dialogContentRef, [], [progress]);

    const setFocus = () => {
      if (progress || !dialogRef.current) return;
      if (headingRef.current) {
        focusNonInteractiveElement(headingRef.current, heading ?? ariaLabel);
      } else {
        focusNonInteractiveElement(dialogRef.current, heading ?? ariaLabel);
      }
    };

    const onCancelHandler = typeof onCancel === 'function' ? onCancel : onCancel.handler;

    useEscape(
      e => {
        if (onDismiss) {
          onDismiss();
        } else {
          onCancelHandler();
        }
        e.stopPropagation();
      },
      dialogRef,
      [onDismiss, onCancelHandler]
    );

    useOuterEvent('mousedown', [target, dialogRef], () => {
      if (onDismiss) {
        onDismiss();
      } else {
        onCancelHandler();
      }
    });

    // Using layout effect to capture the focused element prior to dialog elements taking focus.
    useLayoutEffect(() => {
      const initiatorElement = getActiveElement();

      return () => {
        if (initiatorElement instanceof HTMLElement && initiatorElement.isConnected) {
          initiatorElement.focus();
        }
      };
    }, []);

    useEffect(() => {
      if (!progress) {
        setFocus();
      }
    }, [progress]);

    useEffect(() => {
      const closeDialog = () => {
        setTimeout(() => {
          if (onDismiss) {
            onDismiss();
          } else {
            onCancelHandler();
          }
          // The 0 timeout ensures that the dialog will not re-open immediately after the close event is triggered.
        }, 0);
      };

      target.addEventListener('click', closeDialog);

      return () => {
        target.removeEventListener('click', closeDialog);
      };
    }, []);

    return (
      <StyledFormDialog
        {...restProps}
        {...(heading === undefined
          ? { 'aria-label': ariaLabel }
          : { 'aria-labelledby': headingId })}
        arrow={arrow}
        target={target}
        progress={progress}
        ref={dialogRef}
      >
        {heading !== undefined && (
          <Flex
            container={{
              alignItems: 'center',
              gap: additionalInfo?.heading ? 1 : undefined,
              pad: [2, 2, 1]
            }}
            item={{ shrink: 0 }}
            as={StyledDialogHeader}
          >
            <Text ref={headingRef} id={headingId} variant='h2'>
              {heading}
            </Text>
            {additionalInfo?.heading && (
              <AdditionalInfo heading={additionalInfo.heading}>
                {additionalInfo.children}
              </AdditionalInfo>
            )}
          </Flex>
        )}

        <Flex
          item={{ grow: 1 }}
          as={StyledDialogContent}
          ref={dialogContentRef}
          tabIndex={contentTabIndex}
        >
          {children && (
            <StyledChildrenWrap inert={progress ? '' : undefined}>{children}</StyledChildrenWrap>
          )}
          <Progress
            visible={!!progress}
            focusOnVisible
            placement='local'
            message={typeof progress === 'string' ? progress : undefined}
          />
        </Flex>

        <Flex
          container={{ alignItems: 'center', justify: 'between', pad: 2 }}
          item={{ shrink: 0 }}
          as={StyledDialogFooter}
        >
          <Button
            onClick={() => {
              onCancelHandler();
            }}
            disabled={typeof onCancel !== 'function' ? onCancel.disabled : undefined}
          >
            {typeof onCancel !== 'function' && onCancel.text ? onCancel.text : t('cancel')}
          </Button>

          <Button
            variant='primary'
            onClick={() => {
              (typeof onSubmit !== 'function' ? onSubmit.handler : onSubmit)();
            }}
            disabled={typeof onSubmit !== 'function' ? onSubmit.disabled : undefined}
          >
            {typeof onSubmit !== 'function' && onSubmit.text ? onSubmit.text : t('submit')}
          </Button>
        </Flex>
      </StyledFormDialog>
    );
  }
);

export default FormDialog;
